import { useState } from "react";
import { submitLeadQuietly } from "../lib/crm";
import { motion } from "framer-motion";
import {
  GraduationCap,
  Brain,
  Globe2,
  FileText,
  Users,
  CalendarCheck,
  ClipboardCheck,
  BadgeCheck,
  Mail,
  Phone,
  IndianRupee,
  Star,
  Sparkles,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import Pricing_counseling_page from "./Pricing_counseling_page.jsx";

const services = [
  {
    icon: Brain,
    title: "Psychometric Assessment",
    desc: "Scientific, online career test with a counselor-led debrief to uncover strengths, interests & aptitudes.",
  },
  {
    icon: GraduationCap,
    title: "Career Counselling",
    desc: "1:1 sessions to align student strengths with the right stream, course & career pathways.",
  },
  {
    icon: ClipboardCheck,
    title: "Complete Admission Guidance",
    desc: "End-to-end support for Indian & International UG/PG – shortlisting, timelines, and strategy.",
  },
  {
    icon: CalendarCheck,
    title: "Exam & Deadline Updates",
    desc: "Stay ahead on JEE/NEET/CUET/CAT, SAT/GRE/GMAT/IELTS with reminders & planning.",
  },
  {
    icon: FileText,
    title: "Essays / SOP & Application Review",
    desc: "Impactful edits + final checks so every submission is polished and error-free.",
  },
  {
    icon: Users,
    title: "Interactive one-to-one Sessions",
    desc: "Live Q&As with updates of colleges for real program insight (India & Abroad).",
  },
  {
    icon: BadgeCheck,
    title: "Profile Building",
    desc: "Action plan for extracurriculars, internships & certifications to strengthen applications.",
  },
  {
    icon: Globe2,
    title: "Scholarships & Visa Guidance",
    desc: "Aid/fee planning, scholarship pointers & pre-departure/visa support for study abroad.",
  },
];

const faqs = [
  {
    q: "Is this for Indian AND International admissions?",
    a: "Yes – we support UG/PG applications in India (IITs, NITs, medical, management, design, etc.) and global universities (US, UK, Europe, Canada, Australia).",
  },
  {
    q: "Is the psychometric test included?",
    a: "Yes – it’s a core part of the full package and also included in the 'Only Counselling' plan.",
  },
  {
    q: "How many universities are covered in the package?",
    a: "Up to 5 complete applications are included. Beyond that, it’s ₹5,000 per additional university application.",
  },
  {
    q: "Do you guarantee admission?",
    a: "No. We do not guarantee seats, but our structured process maximizes your chances and reduces errors.",
  },
  {
    q: "Do parents get involved?",
    a: "Absolutely. We align student aspirations with parent expectations through regular joint check-ins.",
  },
];

const perks = [
  "Personalized roadmap for every student",
  "Transparent, student-first guidance (no commissions)",
  "Unlimited mentor support during the cycle",
  "Data-led decisions through psychometric insights",
];

const pulse = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" },
};

// Identifies this form to the CRM: becomes the campaign name and the key its
// Lead Routing rule matches on.
const CRM_FORM_ID = "counseling";

export default function UniThinkCareerLanding() {
  const [mobileCtaOpen, setMobileCtaOpen] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
    phone: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [gotcha, setGotcha] = useState(""); // bot trap; kept out of the Sheet payload

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess(false);
    setError("");

    const cleanPhone = formData.phone.replace(/[\s-]/g, "");

    const phoneRegex = /^[6-9][0-9]{9}$/;
    const allSameDigitRegex = /^(\d)\1{9}$/;
    const isSequential = cleanPhone === "9876543210" || cleanPhone === "9999888877" || cleanPhone === "9898989898";

    if (!phoneRegex.test(cleanPhone) || allSameDigitRegex.test(cleanPhone) || isSequential) {
      setError("Please enter a valid 10-digit phone number.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email address.");
      return;
    }

    setLoading(true);

    try {
      // Double-writing while the CRM proves itself: the Sheet is still the
      // source of truth, so only its outcome decides what the visitor sees.
      const [sheetResult] = await Promise.allSettled([
        fetch("https://script.google.com/macros/s/AKfycbxv_DtcFkKACNHHlG4mhwMuUCMvHUiFKetYPgCdgur9e_uEqkhLFWB4zYOsBiU-TxtVVA/exec", {
          method: "POST",
          mode: "no-cors",
          body: JSON.stringify({ ...formData, phone: cleanPhone }),
          headers: { "Content-Type": "application/json" },
        }),
        submitLeadQuietly({
          formId: CRM_FORM_ID,
          fields: { ...formData, phone: cleanPhone, _gotcha: gotcha },
        }),
      ]);
      if (sheetResult.status === "rejected") throw sheetResult.reason;
      setSuccess(true);
      setFormData({ name: "", email: "", phone: "", message: "" });

      // Fire GTM DataLayer Event
      if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          'event': 'submit_lead_form',
          'form_name': 'counseling_form'
        });
      }
    } catch (err) {
      setError("Error submitting form. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50 text-slate-900">
      {/* NAV / TOP BAR */}
      <header className="sticky top-0 z-30 backdrop-blur bg-white/70 border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 font-semibold text-slate-800">
            <Sparkles className="h-5 w-5" />
            <span>UniThink</span>
            <span className="hidden sm:inline text-slate-400">•</span>
            <span className="hidden sm:inline text-slate-500">Career & Admissions</span>
          </div>
          <div className="hidden sm:flex items-center gap-3">
            <a
              href="#pricing"
              className="px-4 py-2 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 text-sm"
            >
              Pricing
            </a>
            <a
              href="#lead-form"
              className="px-4 py-2 rounded-xl bg-slate-900 text-white hover:bg-slate-800 text-sm"
            >
              Book Session
            </a>
          </div>
          <button
            onClick={() => setMobileCtaOpen((s) => !s)}
            className="sm:hidden inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-300 bg-white"
            aria-expanded={mobileCtaOpen}
            aria-controls="mobile-cta"
          >
            <ArrowRight className={`h-4 w-4 transition-transform ${mobileCtaOpen ? "rotate-90" : ""}`} />
            <span className="text-sm">Menu</span>
          </button>
        </div>
        {mobileCtaOpen && (
          <div id="mobile-cta" className="sm:hidden border-t border-slate-200 bg-white">
            <div className="px-4 py-3 grid grid-cols-1 gap-2">
              <a href="#pricing" className="px-4 py-3 rounded-xl border border-slate-200">Pricing</a>
              <a href="#lead-form" className="px-4 py-3 rounded-xl bg-slate-900 text-white">Book Session</a>
            </div>
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-8 py-12 lg:py-24 items-center">
            <motion.div
              className="lg:col-span-7"
              initial={pulse.initial}
              animate={pulse.animate}
              transition={pulse.transition}
            >
              <p className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-slate-600 bg-slate-100 rounded-full px-3 py-1 mb-3">
                <Star className="h-3 w-3" /> Research Insight
              </p>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">
                What if choosing the <span className="text-slate-900">right career</span> & getting into the <span className="underline decoration-red-400 decoration-4 underline-offset-4">right college</span> could be stress‑free?
              </h1>
              <p className="mt-4 text-slate-600 text-base sm:text-lg max-w-2xl">
                Studies show most young adults struggle with career decisions due to low self-awareness, unclear goals and limited exposure. UniThink turns that maze into a clear map through psychometric assessment, expert career counseling, and end‑to‑end admissions support for India & Abroad (UG/PG).
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <a href="#lead-form" className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-slate-900 text-white hover:bg-slate-800">
                  Book Your Counseling Session
                  <ArrowRight className="h-4 w-4" />
                </a>
                <a href="#services" className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-white border border-slate-200 hover:bg-slate-50">
                  Explore Services
                </a>
              </div>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-600">
                {perks.map((p) => (
                  <li key={p} className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              className="lg:col-span-5"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
            >
              <div id="lead-form" className="relative rounded-3xl bg-gradient-to-br from-red-50 to-slate-50 p-6 sm:p-8 shadow-sm border border-red-100">
                <div className="absolute -top-6 -right-6 bg-red-400 text-slate-900 font-semibold px-4 py-2 rounded-full shadow">
                  Limited Seats
                </div>
                <h3 className="text-xl font-bold">Book Your Counseling Session</h3>
                <p className="mt-1 text-slate-600 text-sm mb-5">Tell us a bit about you. Our team will call back to confirm your slot.</p>
                <form onSubmit={handleSubmit} className="space-y-3 text-black">
                  {/* Bot trap: hidden from people, so anything typed here is spam. */}
                  <input
                    type="text"
                    name="_gotcha"
                    value={gotcha}
                    onChange={(e) => setGotcha(e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                  />
                  <input
                    type="text"
                    name="name"
                    placeholder="Full Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full rounded-xl border p-3 text-sm focus:ring-2 focus:ring-red-200 focus:outline-none"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full rounded-xl border p-3 text-sm focus:ring-2 focus:ring-red-200 focus:outline-none"
                  />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone (10 digits)"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    maxLength="10"
                    pattern="[0-9]{10}"
                    className="w-full rounded-xl border p-3 text-sm focus:ring-2 focus:ring-red-200 focus:outline-none"
                  />
                  <textarea
                    name="message"
                    placeholder="Your Message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={2}
                    className="w-full rounded-xl border p-3 text-sm focus:ring-2 focus:ring-red-200 focus:outline-none"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-xl bg-slate-900 px-4 py-3 text-white font-medium hover:bg-slate-800 shadow mt-2 transition-colors"
                  >
                    {loading ? "Submitting..." : "Submit"}
                  </button>
                  {success && <p className="text-emerald-600 mt-2 text-sm text-center font-medium">✅ Form submitted successfully!</p>}
                  {error && <p className="text-red-500 mt-2 text-sm text-center font-medium">❌ {error}</p>}
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-0 sm:py-16 lg:py-0 bg-slate-50/60 border-t border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto p-0">
            <h2 className="text-2xl sm:text-3xl font-bold m-0 p-0">Transparent Pricing</h2>
            <p className="mt-2 text-slate-600 p-0">Simple plans with everything included. Psychometric Assessment is part of our full packages.</p>
          </div>
        </div>
      </section>
      <Pricing_counseling_page />

      {/* WHY IT MATTERS – RESEARCH COMPARISON */}
      <section className="py-14 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="text-2xl sm:text-3xl font-bold">Why This Matters — Backed by Global Research</h2>
            <p className="mt-2 text-slate-600">Across countries, students face similar challenges: low career awareness, skills gaps, and rising competition for limited seats. Independent studies and education bodies consistently highlight three themes — <span className="font-medium">self‑awareness</span>, <span className="font-medium">structured guidance</span>, and <span className="font-medium">early planning</span> — as the strongest predictors of better college and career outcomes. Here’s a quick comparison of global findings and how UniThink addresses them.</p>
          </div>

          <div className="mt-8 grid lg:grid-cols-2 gap-5">
            {/* Global Research Highlights */}
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-start gap-3 text-slate-900 font-semibold">
                <Globe2 className="h-6 w-6 shrink-0 text-blue-600" />
                <span className="mt-0.5">Global Research Highlights</span>
              </div>
              <ul className="mt-5 space-y-4 text-slate-700 text-sm">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
                  <span className="mt-0.5">Career mismatch and late course changes increase time & cost to degree; early guidance reduces switch rates.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
                  <span className="mt-0.5">Structured counseling + psychometrics improves decision clarity and confidence for students and families.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
                  <span className="mt-0.5">Exposure to options (campus talks, mentor Q&As) broadens aspirations beyond a narrow set of careers.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
                  <span className="mt-0.5">Universities and Fortune‑500 employers widely use aptitude & personality assessments to predict fit and success.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
                  <span className="mt-0.5">Parent involvement, when informed, correlates with higher student persistence and better outcomes.</span>
                </li>
              </ul>
            </div>

            {/* How UniThink Helps */}
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-start gap-3 text-slate-900 font-semibold">
                <Sparkles className="h-6 w-6 shrink-0 text-amber-500" />
                <span className="mt-0.5">How UniThink Turns Research Into Results</span>
              </div>
              <ul className="mt-5 space-y-4 text-slate-700 text-sm">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
                  <span className="mt-0.5">Begin with a science‑backed <span className="font-medium">Psychometric Assessment</span> to build self‑awareness and align strengths with study streams.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
                  <span className="mt-0.5">Provide a structured, phased plan: self‑discovery → shortlisting → applications → interviews → decisions.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
                  <span className="mt-0.5">Expand horizons with <span className="font-medium">interactive university sessions</span> (India & Abroad) and transparent program insights.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
                  <span className="mt-0.5">Reduce rework with expert <span className="font-medium">SOP/essay editing</span>, deadline tracking, and complete application reviews.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
                  <span className="mt-0.5">Involve parents through joint check‑ins so choices are confident and financially planned.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-14 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <h2 className="text-2xl sm:text-3xl font-bold">How We Help</h2>
            <p className="mt-2 text-slate-600">Everything you need — from self‑discovery to offer letters — for Indian & International admissions (UG/PG).</p>
          </div>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {services.map((s) => (
              <motion.div
                key={s.title}
                className="group rounded-3xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow"
                initial={pulse.initial}
                whileInView={pulse.animate}
                viewport={{ once: true, margin: "-50px" }}
                transition={pulse.transition}
              >
                <div className="flex items-center gap-3">
                  <s.icon className="h-5 w-5 text-slate-900" />
                  <h3 className="font-semibold">{s.title}</h3>
                </div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* DETAILS: WHAT'S INCLUDED (from your spec) */}
      <section className="py-14 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold">Everything Included</h2>
              <ul className="mt-5 space-y-3 text-slate-700">
                {[
                  "Career Guidance & End-to-End Undergraduate/PG Application (India & Abroad)",
                  "One-time registration included in package",
                  "Career Counselling (1:1)",
                  "Complete Admission Guidance & Assistance",
                  "Updates on entrance exams & application deadlines",
                  "Scholarship advice & fee planning",
                  "Undergraduate/PG Essays & SOP – edit & review",
                  "Undergraduate application review",
                  "Filling applications for up to 5 colleges/universities",
                  "Interactive sessions with universities (India/Abroad)",
                  "Entrance exam guidelines & prep pointers",
                  "Profile building: activities/internships/certifications",
                  "Document guidance: CV / Portfolio / LOR",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-3xl bg-gradient-to-br from-white to-amber-50 border border-red-100 p-6 shadow-sm">
              <h3 className="text-lg font-semibold flex items-center gap-2"><IndianRupee className="h-5 w-5" />One-Time Comprehensive Package</h3>
              <p className="text-slate-600 text-sm mt-1">All-inclusive, end-to-end admissions support for India & Abroad (UG/PG).</p>
              <div className="mt-4 flex items-end gap-2">
                <span className="text-3xl font-extrabold">Enquire to know more</span>

              </div>
              <p className="mt-2 text-sm text-slate-700">Includes psychometric assessment, full counseling, 5 applications, SOP/essay edits, interview prep & more.</p>
              <a href="#lead-form" className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-slate-900 text-white hover:bg-slate-800">Enroll Today <ArrowRight className="h-4 w-4" /></a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 sm:py-16 lg:py-20 bg-white border-t border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <h2 className="text-2xl sm:text-3xl font-bold">FAQs</h2>
            <p className="mt-2 text-slate-600">Straight answers for students and parents.</p>
          </div>
          <div className="mt-6 divide-y divide-slate-200 rounded-3xl border border-slate-200 bg-slate-50/50">
            {faqs.map((f, i) => (
              <details key={i} className="group open:bg-white open:shadow-sm transition">
                <summary className="list-none cursor-pointer px-5 sm:px-6 py-4 sm:py-5 flex items-start justify-between gap-4">
                  <span className="font-medium text-slate-900">{f.q}</span>
                  <ArrowRight className="h-4 w-4 text-slate-400 group-open:rotate-90 transition" />
                </summary>
                <div className="px-5 sm:px-6 pb-5 -mt-1 text-slate-700 text-sm leading-relaxed">{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>


      {/* FOOTER */}
      <footer className="py-10 text-sm text-slate-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p>©️ {new Date().getFullYear()} UniThink Education International Pvt. Ltd.</p>
          <div className="flex items-center gap-4">
            <a href="tel:+919900911649" className="inline-flex items-center gap-2"><Phone className="h-4 w-4" />Call</a>
            <a href="mailto:info@unithink.in" className="inline-flex items-center gap-2"><Mail className="h-4 w-4" />Email</a>
          </div>
        </div>
      </footer>

      {/* MOBILE STICKY CTA */}
      <div className="fixed inset-x-0 bottom-0 sm:hidden bg-white/95 border-t border-slate-200 p-3 flex items-center justify-between gap-3">
        <div className="text-xs">
          <div className="font-semibold">Ready to begin?</div>
          <div className="text-slate-500">Book your counseling session now</div>
        </div>
        <a href="#lead-form" className="px-4 py-2 rounded-xl bg-slate-900 text-white">Book</a>
      </div>
    </div>
  );
}